/**
 * App-wide snackbar (toast) notifications — the single, consistent way every web
 * app surfaces transient feedback ("Saved", "Could not save", …).
 *
 * Wrap the app in `<SnackbarProvider>` (inside `AppThemeProvider`, so the toast
 * picks up the active theme's severity palette and typography), then call the
 * `useSnackbar()` hook from anywhere below it:
 *
 *   const snackbar = useSnackbar();
 *   snackbar.success(t('admin.bulletins.saveSuccess'));
 *   snackbar.error(t('admin.bulletins.saveError'));
 *
 * Notifications are shown one at a time via a FIFO queue (MUI's consecutive-
 * snackbars pattern): a new toast waits for the current one to exit, so messages
 * never stack or clobber each other. Messages are passed in already localized —
 * this component is i18n-agnostic.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import { X } from 'lucide-react';

export type NotifySeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotifyOptions {
  severity?: NotifySeverity;
  /** Auto-hide delay in ms. Defaults to 6000, or 10000 for errors. */
  duration?: number;
  /**
   * An optional follow-up the message offers, shown as a button beside the
   * dismiss control. For the "we did X, and here is the one thing you might
   * want next" case — an undo, or a cleanup the action deliberately left alone.
   * Choosing it dismisses the snackbar.
   */
  action?: { label: string; onClick: () => void };
}

type SeverityOptions = Omit<NotifyOptions, 'severity'>;

export interface SnackbarApi {
  /** Show a notification with an explicit severity (defaults to `info`). */
  notify: (message: string, options?: NotifyOptions) => void;
  success: (message: string, options?: SeverityOptions) => void;
  error: (message: string, options?: SeverityOptions) => void;
  warning: (message: string, options?: SeverityOptions) => void;
  info: (message: string, options?: SeverityOptions) => void;
}

interface SnackItem {
  key: number;
  message: string;
  severity: NotifySeverity;
  duration: number;
  action?: { label: string; onClick: () => void };
}

const DEFAULT_DURATION = 6000;
const ERROR_DURATION = 10000;

const SnackbarContext = createContext<SnackbarApi | null>(null);

export interface SnackbarProviderProps {
  children: ReactNode;
  /** aria-label for the dismiss button — pass a localized string from the host app. */
  dismissLabel?: string;
  /**
   * Where the toast appears. `top` (the default) is the top-right corner.
   * `bottom` is the bottom-right corner (bottom-centre on a phone) — for an app
   * whose header carries the page title, search and language switch, which a
   * top toast covers for six seconds after every save. Not for an app with a
   * bottom tab bar, which a bottom toast would cover instead.
   */
  placement?: 'top' | 'bottom';
}

export function SnackbarProvider({
  children,
  dismissLabel = 'Dismiss',
  placement = 'top',
}: SnackbarProviderProps) {
  const [queue, setQueue] = useState<SnackItem[]>([]);
  const [current, setCurrent] = useState<SnackItem | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const keyRef = useRef(0);

  // Promote the next queued item once nothing is showing; if one is still open,
  // close it first and let `onExited` clear `current` so the next can take over.
  useEffect(() => {
    if (queue.length && !current) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    } else if (queue.length && current && open) {
      setOpen(false);
    }
  }, [queue, current, open]);

  const enqueue = useCallback((message: string, options?: NotifyOptions) => {
    const severity = options?.severity ?? 'info';
    const duration =
      options?.duration ?? (severity === 'error' ? ERROR_DURATION : DEFAULT_DURATION);
    keyRef.current += 1;
    setQueue((prev) => [
      ...prev,
      { key: keyRef.current, message, severity, duration, action: options?.action },
    ]);
  }, []);

  const api = useMemo<SnackbarApi>(
    () => ({
      notify: enqueue,
      success: (message, options) => enqueue(message, { ...options, severity: 'success' }),
      error: (message, options) => enqueue(message, { ...options, severity: 'error' }),
      warning: (message, options) => enqueue(message, { ...options, severity: 'warning' }),
      info: (message, options) => enqueue(message, { ...options, severity: 'info' }),
    }),
    [enqueue],
  );

  // Ignore click-away dismissals so a stray click doesn't swallow feedback.
  const handleClose = useCallback((_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={current?.duration}
        onClose={handleClose}
        // Web apps anchor feedback at the top-right corner (the mobile app uses a
        // bottom-centre pill — see mobile's ToastProvider). On the narrowest
        // viewports we fall back to top-centre so the toast isn't clipped.
        anchorOrigin={{ vertical: placement, horizontal: 'right' }}
        sx={{
          '&.MuiSnackbar-root': {
            [placement]: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 'auto' },
          },
        }}
        slotProps={{ transition: { onExited: () => setCurrent(undefined) } }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            action={
              <>
                {current.action && (
                  <Button
                    size="small"
                    color="inherit"
                    sx={{ fontWeight: 700, textDecoration: 'underline' }}
                    onClick={() => {
                      current.action?.onClick();
                      handleClose(null);
                    }}
                  >
                    {current.action.label}
                  </Button>
                )}
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label={dismissLabel}
                  onClick={handleClose}
                >
                  <X size={16} />
                </IconButton>
              </>
            }
            sx={{
              alignItems: 'center',
              borderRadius: 2,
              boxShadow: 6,
              minWidth: { xs: 'auto', sm: 360 },
            }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within a SnackbarProvider');
  return ctx;
}
